/**
 * TopicsBadges Component
 *
 * Renders topic badges for a journal article. Active topics are shown as
 * filled badges, while non-topics are shown as outline badges with
 * strikethrough text to indicate the source does not cover that topic.
 */

import { Badge } from "@/components/ui/badge"
import { topicLabelMap } from "@/services/journalArticlesService"

/** Props for the TopicsBadges component. */
interface TopicsBadgesProps {
  /** List of topic slugs the article covers. */
  topics?: string[]
  /** List of topic slugs the article does NOT cover. */
  nonTopics?: string[]
  wrap?: boolean
}

/**
 * Renders active and inactive topic badges.
 *
 * @param props - Component props.
 * @param props.topics - Topics the article covers (rendered as filled badges).
 * @param props.nonTopics - Topics the article doesn't cover (rendered with strikethrough).
 */
export default function TopicsBadges({ topics, nonTopics, wrap = true }: TopicsBadgesProps): React.JSX.Element {
  return (
    <div className={`flex gap-1 ${wrap ? "flex-wrap" : ""}`}>
      {topics?.map(topic => (
        <Badge key={topic} variant="outline">
          {topicLabelMap[topic] ?? topic}
        </Badge>
      ))}
      {nonTopics?.map(topic => (
        <Badge key={topic} variant="outline">
          <span className="line-through">{topicLabelMap[topic] ?? topic}</span>
        </Badge>
      ))}
    </div>
  )
}
